import React from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { CollapsibleSection } from "./CollapsibleSection";
import { EXIT_REASONS, OTHER_EXIT_REASON, exitReasonLabel } from "../../lib/exitReasons";


// Consent form (Photo Release / OTC Medical Authorization) checkbox with a
// companion expiry status. Checking / re-signing stamps signed=today &
// expiry=+1yr on the backend (via the `<key>_sign_now` control flag).
function consentStatus(expiry) {
  if (!expiry) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(`${expiry}T00:00:00`);
  const days = Math.round((exp - today) / 86400000);
  if (days < 0) return { tone: "bg-red-100 text-red-700 border-red-200", label: `Expired ${Math.abs(days)}d ago` };
  if (days <= 30) return { tone: "bg-amber-100 text-amber-700 border-amber-200", label: `Expires in ${days}d` };
  return { tone: "bg-emerald-100 text-emerald-700 border-emerald-200", label: `Valid until ${expiry}` };
}

function ConsentForm({ label, keyName, student, editing, editData, setEditData, testid }) {
  const checked = editing ? !!editData[keyName] : !!student[keyName];
  const expiry = student[`${keyName}_expiry`];
  const signed = student[`${keyName}_signed`];
  const signNow = !!editData[`${keyName}_sign_now`];
  const wasChecked = !!student[keyName];
  const status = checked ? consentStatus(expiry) : null;

  return (
    <div className="space-y-1" data-testid={`consent-${testid}`}>
      <div className="flex items-center space-x-2">
        {editing ? (
          <Checkbox
            checked={checked}
            data-testid={`consent-${testid}-checkbox`}
            onCheckedChange={(c) =>
              setEditData((p) => ({ ...p, [keyName]: c, ...(c ? {} : { [`${keyName}_sign_now`]: false }) }))
            }
          />
        ) : (
          <Checkbox checked={checked} disabled />
        )}
        <Label>{label}</Label>
        {status && (
          <span className={`text-xs px-2 py-0.5 rounded-full border ${status.tone}`} data-testid={`consent-${testid}-status`}>
            {status.label}
          </span>
        )}
      </div>
      {checked && (
        <div className="pl-6 text-xs text-muted-foreground space-y-0.5">
          <p data-testid={`consent-${testid}-expiry`}>Expires: {expiry || "—"}</p>
          <p>Signed: {signed || "unknown (pre-tracking)"}</p>
          {editing && (
            wasChecked ? (
              <label className="flex items-center gap-2 pt-1 cursor-pointer text-slate-700">
                <Checkbox
                  checked={signNow}
                  data-testid={`consent-${testid}-signnow`}
                  onCheckedChange={(c) => setEditData((p) => ({ ...p, [`${keyName}_sign_now`]: c }))}
                />
                <span>Re-sign today (resets expiry to one year out)</span>
              </label>
            ) : (
              <p className="pt-1 text-slate-500 italic">Will be dated today with a 1-year expiry on save.</p>
            )
          )}
        </div>
      )}
    </div>
  );
}


export function DemographicsTab({ student, editing, editData, setEditData, districts, clinicians, specialEducators, advisoryGroups, classGroupConfig, customGroups }) {
  const getStaffName = (staffId, staffList) => {
    const staff = staffList.find((s) => s.id === staffId);
    return staff ? staff.name : "-";
  };

  // Structured mailing address helpers — keep a clean composed `mailing_address`
  // (used for printed labels) in sync with the street / city / state / zip fields.
  const composeMailing = (street, city, state, zip) => {
    const csz = [city && (state || zip) ? `${city},` : city, state, zip].filter(Boolean).join(" ").trim();
    return [street, csz].filter(Boolean).join("\n");
  };
  const updateMailing = (field, value) => {
    setEditData((p) => {
      const next = { ...p, [field]: value };
      next.mailing_address = composeMailing(next.mailing_street, next.mailing_city, next.mailing_state, next.mailing_zip);
      return next;
    });
  };
  const formatMailing = (s) => (s.mailing_street
    ? composeMailing(s.mailing_street, s.mailing_city, s.mailing_state, s.mailing_zip)
    : (s.mailing_address || ""));

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <CollapsibleSection id="basic-info" title="Basic Information" color="blue" testid="section-basic-info">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>First Name</Label>
              {editing ? (
                <Input value={editData.first_name} onChange={(e) => setEditData((p) => ({ ...p, first_name: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.first_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Middle Name</Label>
              {editing ? (
                <Input value={editData.middle_name || ""} onChange={(e) => setEditData((p) => ({ ...p, middle_name: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.middle_name || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Last Name</Label>
              {editing ? (
                <Input value={editData.last_name} onChange={(e) => setEditData((p) => ({ ...p, last_name: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.last_name}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Preferred Name</Label>
              {editing ? (
                <Input value={editData.preferred_name || ""} onChange={(e) => setEditData((p) => ({ ...p, preferred_name: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.preferred_name || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Pronouns</Label>
              {editing ? (
                <Select value={editData.pronouns || ""} onValueChange={(v) => setEditData((p) => ({ ...p, pronouns: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="he/him">He/Him</SelectItem>
                    <SelectItem value="she/her">She/Her</SelectItem>
                    <SelectItem value="they/them">They/Them</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2">{student.pronouns || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              {editing ? (
                <Input type="date" value={editData.dob} onChange={(e) => setEditData((p) => ({ ...p, dob: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.dob}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Gender</Label>
              {editing ? (
                <Input value={editData.gender || ""} onChange={(e) => setEditData((p) => ({ ...p, gender: e.target.value }))} placeholder="e.g., Male, Female, Non-binary" data-testid="student-gender-input" />
              ) : (
                <p className="text-sm py-2">{student.gender || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Grade Level</Label>
              {editing ? (
                <Input value={editData.grade || ""} onChange={(e) => setEditData((p) => ({ ...p, grade: e.target.value }))} placeholder="e.g., 6th, 7th, 8th" data-testid="student-grade-input" />
              ) : (
                <p className="text-sm py-2">{student.grade || "-"}</p>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Enrollment Status */}
      <CollapsibleSection id="enrollment-status" title="Enrollment Status" color="emerald" testid="section-enrollment-status">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              {editing ? (
                <Select value={editData.status || "current"} onValueChange={(v) => setEditData((p) => ({ ...p, status: v, graduation_status: v === "graduated" ? "graduated" : "", end_date: (v === "current" || v === "referral") ? "" : p.end_date }))}>
                  <SelectTrigger data-testid="student-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Current Student</SelectItem>
                    <SelectItem value="former">Former Student</SelectItem>
                    <SelectItem value="graduated">Graduated</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2">
                  <span className={`px-2 py-0.5 rounded ${student.status === 'graduated' ? 'bg-purple-100 text-purple-700' : student.status === 'referral' ? 'bg-orange-100 text-orange-700' : student.status === 'former' ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                    {student.status === 'graduated' ? 'Graduated' : student.status === 'referral' ? 'Referral' : student.status === 'former' ? 'Former Student' : 'Current Student'}
                  </span>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              {editing ? (
                <Input type="date" value={editData.start_date || ""} onChange={(e) => setEditData((p) => ({ ...p, start_date: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.start_date || "Not set"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              {editing ? (
                <Input type="date" value={editData.end_date || ""} onChange={(e) => {
                  const val = e.target.value;
                  setEditData((p) => ({ ...p, end_date: val, status: p.status === "graduated" ? "graduated" : (val ? "former" : "current") }));
                }} />
              ) : (
                <p className="text-sm py-2">{student.end_date || "N/A (Current)"}</p>
              )}
            </div>
          </div>
          {["former", "graduated"].includes((editing ? editData.status : student.status) || "") && (
            <div className="mt-4 pt-3 border-t space-y-4" data-testid="exit-details">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Exit Reason</Label>
                  {editing ? (
                    <>
                      <Select
                        value={editData.exit_reason || ""}
                        onValueChange={(v) => setEditData((p) => ({ ...p, exit_reason: v, exit_reason_other: v === OTHER_EXIT_REASON ? p.exit_reason_other : "" }))}
                      >
                        <SelectTrigger data-testid="exit-reason-select"><SelectValue placeholder="Select an exit reason…" /></SelectTrigger>
                        <SelectContent>
                          {EXIT_REASONS.map((r) => (
                            <SelectItem key={r} value={r} data-testid={`exit-reason-${r}`}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {editData.exit_reason === OTHER_EXIT_REASON && (
                        <Input
                          placeholder="Please specify the reason"
                          value={editData.exit_reason_other || ""}
                          data-testid="exit-reason-other-input"
                          onChange={(e) => setEditData((p) => ({ ...p, exit_reason_other: e.target.value }))}
                        />
                      )}
                    </>
                  ) : (
                    <p className="text-sm py-2" data-testid="exit-reason-value">
                      {exitReasonLabel(student.exit_reason, student.exit_reason_other) || (
                        <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium">Needs review — no exit reason set</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Departure Notes</Label>
                {editing ? (
                  <Textarea
                    placeholder="Any additional context about this student's departure"
                    value={editData.departure_notes || ""}
                    rows={4}
                    data-testid="departure-notes-input"
                    onChange={(e) => setEditData((p) => ({ ...p, departure_notes: e.target.value }))}
                  />
                ) : (
                  <p className="text-sm py-2 whitespace-pre-wrap" data-testid="departure-notes-value">{student.departure_notes || "None recorded"}</p>
                )}
              </div>
            </div>
          )}
          <div className="mt-4 pt-3 border-t">
            <div className="flex items-center gap-3">
              <Checkbox
                id="summer-school"
                checked={editing ? (editData.attending_summer_school || false) : (student.attending_summer_school || false)}
                onCheckedChange={(checked) => {
                  if (editing) setEditData((p) => ({ ...p, attending_summer_school: !!checked }));
                }}
                disabled={!editing}
                data-testid="summer-school-checkbox"
              />
              <Label htmlFor="summer-school" className="cursor-pointer text-sm font-medium">
                Attending Summer School
              </Label>
              {(editing ? editData.attending_summer_school : student.attending_summer_school) && (
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium">Summer Program</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-7">When checked, this student will be included in summer program attendance and assessments.</p>
          </div>
        </div>
      </CollapsibleSection>

      {/* First Contact */}
      <CollapsibleSection id="first-contact" title="First Contact" color="violet" testid="section-first-contact">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Relationship</Label>
              {editing ? (
                <Input value={editData.first_contact_relationship || ""} onChange={(e) => setEditData((p) => ({ ...p, first_contact_relationship: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.first_contact_relationship || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              {editing ? (
                <Input value={editData.first_contact_name || ""} onChange={(e) => setEditData((p) => ({ ...p, first_contact_name: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.first_contact_name || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              {editing ? (
                <Input value={editData.first_contact_phone || ""} onChange={(e) => setEditData((p) => ({ ...p, first_contact_phone: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.first_contact_phone || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              {editing ? (
                <Input value={editData.first_contact_email || ""} onChange={(e) => setEditData((p) => ({ ...p, first_contact_email: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.first_contact_email || "-"}</p>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Second Contact */}
      <CollapsibleSection id="second-contact" title="Second Contact" color="indigo" testid="section-second-contact">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Relationship</Label>
              {editing ? (
                <Input value={editData.second_contact_relationship || ""} onChange={(e) => setEditData((p) => ({ ...p, second_contact_relationship: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.second_contact_relationship || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              {editing ? (
                <Input value={editData.second_contact_name || ""} onChange={(e) => setEditData((p) => ({ ...p, second_contact_name: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.second_contact_name || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              {editing ? (
                <Input value={editData.second_contact_phone || ""} onChange={(e) => setEditData((p) => ({ ...p, second_contact_phone: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.second_contact_phone || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              {editing ? (
                <Input value={editData.second_contact_email || ""} onChange={(e) => setEditData((p) => ({ ...p, second_contact_email: e.target.value }))} />
              ) : (
                <p className="text-sm py-2">{student.second_contact_email || "-"}</p>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* School & Groups */}
      <CollapsibleSection id="school-groups" title="School & Groups" color="teal" testid="section-school-groups">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>School District</Label>
              {editing ? (
                <Select value={editData.school_district || ""} onValueChange={(v) => setEditData((p) => ({ ...p, school_district: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {districts.map((d) => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2">{student.school_district || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Morning Group</Label>
              {editing ? (
                <Select value={editData.morning_group} onValueChange={(v) => setEditData((p) => ({ ...p, morning_group: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...(classGroupConfig.morning_groups || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((g) => (
                      <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2">{student.morning_group ? (classGroupConfig.morning_groups?.find(g => g.key === student.morning_group)?.name || `Group ${student.morning_group}`) : "Not Assigned"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Afternoon Group</Label>
              {editing ? (
                <Select value={editData.afternoon_group} onValueChange={(v) => setEditData((p) => ({ ...p, afternoon_group: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...(classGroupConfig.afternoon_groups || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((g) => (
                      <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2">{student.afternoon_group ? (classGroupConfig.afternoon_groups?.find(g => g.key === student.afternoon_group)?.name || `Group ${student.afternoon_group}`) : "Not Assigned"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Advisory</Label>
              {editing ? (
                <Select value={editData.advisory_group || "none"} onValueChange={(v) => setEditData((p) => ({ ...p, advisory_group: v === "none" ? null : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select advisory..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Assigned</SelectItem>
                    {advisoryGroups.map((g) => (
                      <SelectItem key={g.group} value={g.group}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2">{student.advisory_group ? (advisoryGroups.find(g => g.group === student.advisory_group)?.name || `Advisory ${student.advisory_group}`) : "Not Assigned"}</p>
              )}
            </div>
          </div>

          {/* Custom Groups / Tags */}
          <div className="pt-4 border-t">
            <Label className="text-sm font-medium mb-2 block">Groups & Tags</Label>
            {editing ? (
              <div className="flex flex-wrap gap-1.5 p-2 border rounded-md bg-white min-h-[40px]" data-testid="student-groups-editor">
                {/* Custom groups from settings */}
                {(customGroups || []).map(g => {
                  const selected = (editData.custom_groups || []).includes(g.name);
                  return (
                    <button key={g.name} type="button"
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selected ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                      onClick={() => {
                        const groups = editData.custom_groups || [];
                        const updated = selected ? groups.filter(n => n !== g.name) : [...groups, g.name];
                        setEditData(p => ({ ...p, custom_groups: updated }));
                      }}
                    >{g.name}</button>
                  );
                })}
                {/* Fixed options */}
                {["Tutoring", "Alternate Plan"].map(tag => {
                  const selected = (editData.custom_groups || []).includes(tag);
                  return (
                    <button key={tag} type="button"
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selected ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100"}`}
                      onClick={() => {
                        const groups = editData.custom_groups || [];
                        const updated = selected ? groups.filter(n => n !== tag) : [...groups, tag];
                        setEditData(p => ({ ...p, custom_groups: updated }));
                      }}
                    >{tag}</button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="student-groups-display">
                {(student.custom_groups || []).length > 0 ? (
                  (student.custom_groups || []).map(g => (
                    <span key={g} className={`px-3 py-1 rounded-full text-xs font-medium ${
                      ["Tutoring", "Alternate Plan"].includes(g) ? "bg-indigo-100 text-indigo-700" : "bg-teal-100 text-teal-700"
                    }`}>{g}</span>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground py-1">No groups assigned</p>
                )}
              </div>
            )}
          </div>

          {/* Assigned Staff */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div className="space-y-2">
              <Label>Assigned Clinician</Label>
              {editing ? (
                <Select 
                  value={editData.assigned_clinician || "none"} 
                  onValueChange={(v) => setEditData((p) => ({ ...p, assigned_clinician: v === "none" ? "" : v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select clinician..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {clinicians.filter((c) => (c.status || "current") !== "former").map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2">{getStaffName(student.assigned_clinician, clinicians)}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Assigned Special Educator</Label>
              {editing ? (
                <Select 
                  value={editData.assigned_special_educator || "none"} 
                  onValueChange={(v) => setEditData((p) => ({ ...p, assigned_special_educator: v === "none" ? "" : v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select special educator..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {specialEducators.map((se) => (
                      <SelectItem key={se.id} value={se.id}>{se.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2">{getStaffName(student.assigned_special_educator, specialEducators)}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 pt-4">
            <div className="flex-1">
              <ConsentForm
                label="Photos Allowed"
                keyName="photos_allowed"
                testid="photo"
                student={student}
                editing={editing}
                editData={editData}
                setEditData={setEditData}
              />
            </div>
            <div className="flex-1">
              <ConsentForm
                label="OTC Medications"
                keyName="otc_medications"
                testid="otc"
                student={student}
                editing={editing}
                editData={editData}
                setEditData={setEditData}
              />
            </div>
          </div>

          <div className="pt-2 space-y-1">
            <Label>OTC Medical Notes</Label>
            {editing ? (
              <Textarea
                value={editData.otc_notes || ""}
                onChange={(e) => setEditData((p) => ({ ...p, otc_notes: e.target.value }))}
                placeholder="Notes about OTC / medical (e.g., Tylenol if needed, dosing, allergies)"
                data-testid="otc-notes-input"
              />
            ) : (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="otc-notes-value">
                {student.otc_notes || "—"}
              </p>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Technology & Device Info */}
      <CollapsibleSection id="technology" title="Technology & Device Info" color="cyan" testid="section-technology">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Student Email / Login</Label>
              {editing ? (
                <Input value={editData.student_email || ""} onChange={(e) => setEditData((p) => ({ ...p, student_email: e.target.value }))} placeholder="student@school.edu" data-testid="student-email-input" />
              ) : (
                <p className="text-sm py-2">{student.student_email || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Chromebook #</Label>
              {editing ? (
                <Input value={editData.chromebook_number || ""} onChange={(e) => setEditData((p) => ({ ...p, chromebook_number: e.target.value }))} placeholder="CB-101" data-testid="chromebook-number-input" />
              ) : (
                <p className="text-sm py-2">{student.chromebook_number || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Current Password</Label>
              {editing ? (
                <Input value={editData.student_password || ""} onChange={(e) => setEditData((p) => ({ ...p, student_password: e.target.value }))} placeholder="Current password" data-testid="student-password-input" />
              ) : (
                <p className="text-sm py-2 font-mono">{student.student_password || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Parent Account / Staff Reset</Label>
              {editing ? (
                <Input value={editData.parent_account || ""} onChange={(e) => setEditData((p) => ({ ...p, parent_account: e.target.value }))} placeholder="Parent account info" data-testid="parent-account-input" />
              ) : (
                <p className="text-sm py-2">{student.parent_account || "-"}</p>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Addresses */}
      <CollapsibleSection id="addresses" title="Addresses" color="amber" testid="section-addresses">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Mailing Address <span className="text-xs text-muted-foreground">(used for printed labels)</span></Label>
              {editing ? (
                <div className="space-y-2">
                  <Input
                    value={editData.mailing_street || ""}
                    onChange={(e) => updateMailing("mailing_street", e.target.value)}
                    placeholder="Street address (e.g., 123 Main St)"
                    data-testid="mailing-street-input"
                  />
                  <div className="grid grid-cols-6 gap-2">
                    <Input className="col-span-3" value={editData.mailing_city || ""} onChange={(e) => updateMailing("mailing_city", e.target.value)} placeholder="City" data-testid="mailing-city-input" />
                    <Input className="col-span-1" value={editData.mailing_state || ""} onChange={(e) => updateMailing("mailing_state", e.target.value.toUpperCase())} placeholder="ST" maxLength={2} data-testid="mailing-state-input" />
                    <Input className="col-span-2" value={editData.mailing_zip || ""} onChange={(e) => updateMailing("mailing_zip", e.target.value)} placeholder="ZIP" data-testid="mailing-zip-input" />
                  </div>
                  {!editData.mailing_street && editData.mailing_address && (
                    <p className="text-xs text-amber-600">Existing address on file: {editData.mailing_address}. Re-enter it in the fields above for correct label printing.</p>
                  )}
                </div>
              ) : (
                <p className="text-sm py-2 whitespace-pre-wrap">{formatMailing(student) || "-"}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Address Notes <span className="text-xs text-muted-foreground">(apt #, directions, etc. — NOT printed on labels)</span></Label>
              {editing ? (
                <Textarea
                  value={editData.mailing_notes || ""}
                  onChange={(e) => setEditData((p) => ({ ...p, mailing_notes: e.target.value }))}
                  placeholder="e.g., Apt 4B; use side entrance; mail to grandparent"
                  data-testid="mailing-notes-input"
                />
              ) : (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="mailing-notes-value">{student.mailing_notes || "—"}</p>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Self-Assessment Code */}
      <CollapsibleSection id="self-assessment" title="Self-Assessment Access" color="rose" testid="section-self-assessment">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Access Code</Label>
            {editing ? (
              <Input
                value={editData.self_assessment_code || ""}
                onChange={(e) => setEditData({...editData, self_assessment_code: e.target.value.toUpperCase()})}
                placeholder="e.g., A1B2C3"
                className="font-mono"
                data-testid="edit-self-assessment-code"
              />
            ) : (
              <p className="text-sm py-2 font-mono font-semibold tracking-wider">{student.self_assessment_code || <span className="text-muted-foreground font-normal">No code assigned</span>}</p>
            )}
          </div>
          {!editing && student.self_assessment_code && (
            <div className="space-y-2">
              <Label>Login Instructions</Label>
              <p className="text-sm text-muted-foreground">Student enters this code on the Self-Assessment login page to access their assessment form.</p>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
